import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import FleetPnlReports from './FleetPnlReports';
import { parseFleetIntelligenceQuery } from './pnlDateRanges';
import './FleetIntelligence.css';

const card = {
  background: 'var(--paper)',
  borderRadius: 12,
  padding: 20,
  boxShadow: 'var(--shadow-sm)',
  border: '1px solid var(--line)',
};

const label = {
  fontSize: 11,
  color: 'var(--ink-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 4,
  display: 'block',
};

function SummaryCard({ title, value, sub, warn }) {
  return (
    <div style={card}>
      <span style={label}>{title}</span>
      <div style={{
        fontSize: 24,
        fontWeight: 700,
        color: warn ? 'var(--danger)' : 'var(--ink-900)',
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function IftaReport({ headers }) {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getUTCMonth() / 3) + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/fleet-intelligence/ifta/report', {
        headers,
        params: { year, quarter, _t: Date.now() },
      });
      setReport(response.data);
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.message || err.message || 'IFTA report failed');
    }
    setLoading(false);
  }, [headers, quarter, year]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const rows = report?.rows || [];
    const body = rows.map((row) => [
      row.unit,
      row.jurisdiction,
      row.miles,
      row.total,
    ].join(','));
    const blob = new Blob([
      ['unit,jurisdiction,miles,total', ...body].join('\n') + '\n',
    ], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ifta-${report?.quarterTag || `${year}-Q${quarter}`}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>IFTA Jurisdiction Summary</div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
              Built from the Motive IFTA summary shelf
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={label}>Year</label>
              <input
                type="number"
                min="2020"
                max="2100"
                value={year}
                onChange={(event) => setYear(parseInt(event.target.value, 10) || now.getUTCFullYear())}
              />
            </div>
            <div>
              <label style={label}>Quarter</label>
              <select value={quarter} onChange={(event) => setQuarter(parseInt(event.target.value, 10))}>
                {[1, 2, 3, 4].map((value) => (
                  <option key={value} value={value}>Q{value}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Load'}
            </button>
            <button type="button" onClick={exportCsv} disabled={!report?.rows?.length}>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && <div className="fd-pnl-error">{error}</div>}
      {loading && <div className="fd-pnl-loading">Loading IFTA report…</div>}
      {!loading && report && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}>
            <SummaryCard title="Quarter" value={report.quarterTag || '—'} />
            <SummaryCard
              title="Fleet IFTA Miles"
              value={Number(report.totals?.fleetIftaMiles || 0).toLocaleString()}
            />
            <SummaryCard
              title="Archive Odometer Miles"
              value={Number(report.totals?.fleetArchiveMiles || 0).toLocaleString()}
            />
            <SummaryCard
              title="Flagged Units"
              value={String(report.totals?.flaggedUnits || 0)}
              warn={(report.totals?.flaggedUnits || 0) > 0}
            />
          </div>

          <div style={{ ...card, marginBottom: 16, overflowX: 'auto' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Jurisdiction miles</div>
            {!report.rows?.length ? (
              <div style={{ color: 'var(--ink-500)' }}>No shelf rows for this quarter.</div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr><th>Unit</th><th>Jurisdiction</th><th>Miles</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {report.rows.map((row, index) => (
                    <tr key={`${row.unit}-${row.jurisdiction}-${index}`}>
                      <td>{row.unit}</td>
                      <td>{row.jurisdiction}</td>
                      <td>{Number(row.miles).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td>{Number(row.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ ...card, overflowX: 'auto' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Reconciliation</div>
            <table className="tbl">
              <thead>
                <tr><th>Unit</th><th>IFTA mi</th><th>Archive mi</th><th>Δ mi</th><th>Δ %</th><th>Flag</th></tr>
              </thead>
              <tbody>
                {(report.reconciliation || []).map((row) => (
                  <tr key={row.unitNumber}>
                    <td>{row.unitNumber}</td>
                    <td>{Number(row.iftaJurisdictionMiles).toLocaleString()}</td>
                    <td>{Number(row.archiveOdometerMiles).toLocaleString()}</td>
                    <td>{Number(row.deltaMiles).toLocaleString()}</td>
                    <td>{row.deltaPct}%</td>
                    <td style={{
                      color: row.flagged ? 'var(--danger)' : 'var(--success)',
                      fontWeight: 600,
                    }}>
                      {row.flagged ? 'FLAG' : 'OK'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const FleetIntelligence = () => {
  const token = localStorage.getItem('token');
  const headers = { 'x-auth-token': token };
  const query = parseFleetIntelligenceQuery(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const initialView = ['lanes', 'ifta'].includes(query.view) ? query.view : 'reports';
  const [view, setView] = useState(initialView);

  return (
    <div className="fd-fleet-page" style={{
      padding: 24,
      paddingBottom: 80,
      background: 'var(--cream-50)',
      minHeight: '100vh',
      overflowY: 'auto',
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Fleet Intelligence</h1>
        <p style={{ color: 'var(--ink-500)', margin: '2px 0 0', fontSize: 12 }}>
          Company P&amp;L uses the margin-before-driver engine.
        </p>
      </div>

      <div style={{
        display: 'flex',
        gap: 4,
        marginBottom: 20,
        borderBottom: '2px solid var(--line)',
      }}>
        {[
          { key: 'reports', label: 'P&L Reports' },
          { key: 'lanes', label: 'Lane Analysis' },
          { key: 'ifta', label: 'IFTA' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setView(tab.key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              cursor: 'pointer',
              background: 'transparent',
              fontWeight: 600,
              color: view === tab.key ? 'var(--brand-500)' : 'var(--ink-500)',
              borderBottom: view === tab.key
                ? '2px solid var(--brand-500)'
                : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'reports' && <FleetPnlReports headers={headers} initialQuery={query} />}
      {view === 'lanes' && (
        <div style={card}>
          <div className="eyebrow">Lane Analysis</div>
          <h2 className="page-title" style={{ margin: '6px 0' }}>Lane history is being rebuilt</h2>
          <p style={{ color: 'var(--ink-500)', margin: 0 }}>
            Historical lane rankings are unavailable until the new margin-before-driver ledger is complete.
          </p>
        </div>
      )}
      {view === 'ifta' && <IftaReport headers={headers} />}
    </div>
  );
};

export default FleetIntelligence;
